import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { z } from 'zod'
import {
	useCreateAppointment,
	useUpdateAppointment,
	useDeleteAppointment,
	transformFormDataForCreate,
	transformFormDataForUpdate,
} from '@/hooks/useAppointmentMutation'
import { useAppointmentDetail } from '@/hooks/useCalendarEvents'
import { trpc } from '@/trpc/client'
import { formatDateForApi, addMinutesToTime } from '@/lib/calendar-utils'
import { DEFAULT_EVENT_COLOR, getActionTypes, CALENDAR_COLORS, STATUS_BACKGROUNDS, isCancelledOnly } from '@/lib/constants'
import { MAX_PP_PER_DAY } from '@/config'
import type { DayStats } from '@/hooks/useCalendarEvents'
import type {
	CalendarEvent,
	DialogMode,
	AppointmentType,
	AppointmentFormData,
	PatientSearchResult,
	RepeatFrequency,
} from '@/types/calendar'

export type ViewMode = 'zub' | 'dh' | 'reception'

export interface PatientRowData {
	actionType: string
	priceFrom: string
	priceTo: string
}

const TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/

function timeToMinutes(time: string): number {
	const [h, m] = time.split(':').map(Number)
	return h * 60 + m
}

export const appointmentFormSchema = z
	.object({
		title: z.string().min(1, 'Název je povinný'),
		date: z.string().min(1, 'Datum je povinné'),
		startTime: z.string().regex(TIME_REGEX, 'Neplatný formát času'),
		endTime: z.string().regex(TIME_REGEX, 'Neplatný formát času'),
		note: z.string().optional(),
		doctorsNote: z.string().optional(),
		background: z.string().optional(),
		repeatAfter: z.enum(['NONE', 'DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY']),
		endDate: z.string().optional(),
	})
	.superRefine((data, ctx) => {
		if (!TIME_REGEX.test(data.startTime) || !TIME_REGEX.test(data.endTime)) return
		if (timeToMinutes(data.endTime) <= timeToMinutes(data.startTime)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ['endTime'],
				message: 'Konec musí být po začátku',
			})
		}
	})

export type AppointmentFormValues = z.infer<typeof appointmentFormSchema>

export interface AppointmentDialogProps {
	open: boolean
	onClose: () => void
	mode: DialogMode
	event: CalendarEvent | null
	defaults: Partial<AppointmentFormData> | null
	calendarType: AppointmentType
	viewMode?: ViewMode
	dayStats?: Record<string, DayStats>
}

function extractBirthYear(personalId: string): string {
	if (!personalId || personalId.length < 2) return ''
	return personalId.slice(0, 2)
}

function formatPatientForTitle(patient: PatientSearchResult): string {
	const birthYear = extractBirthYear(patient.personalId)
	return `${patient.lastName} ${patient.firstName} ${birthYear}`
}

function generateTitleFromPatients(patients: PatientSearchResult[]): string {
	return patients.map(formatPatientForTitle).join(', ')
}

/**
 * Headless hook containing all appointment-dialog state, form handling, and mutations.
 * Variant components consume the returned state/handlers and render arbitrary UI.
 */
export function useAppointmentDialog(props: AppointmentDialogProps) {
	const {
		open,
		onClose,
		mode,
		event,
		defaults,
		calendarType,
		viewMode = 'reception',
		dayStats = {},
	} = props

	const [selectedPatients, setSelectedPatients] = useState<PatientSearchResult[]>([])
	const [patientData, setPatientData] = useState<Record<string, PatientRowData>>({})
	const [isDeleting, setIsDeleting] = useState(false)
	const [showPatientsFade, setShowPatientsFade] = useState(false)
	const patientsListRef = useRef<HTMLDivElement>(null)
	const [patientsToDailyNotes, setPatientsToDailyNotes] = useState<Set<string>>(new Set())
	const [noShowUpPaid, setNoShowUpPaid] = useState(false)
	const [sendCancelSms, setSendCancelSms] = useState(false)
	const [cancelType, setCancelType] = useState<string>('')

	const [patientQuery, setPatientQuery] = useState('')
	const [patientDropdownOpen, setPatientDropdownOpen] = useState(false)
	const [patientSelectedIndex, setPatientSelectedIndex] = useState(0)

	const [showGhostColumn, setShowGhostColumn] = useState(false)
	const toggleGhostColumn = useCallback(() => setShowGhostColumn((v) => !v), [])

	const checkPatientsFade = useCallback(() => {
		const el = patientsListRef.current
		if (!el) return
		const isOverflowing = el.scrollHeight > el.clientHeight
		const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 4
		setShowPatientsFade(isOverflowing && !isAtBottom)
	}, [])

	useEffect(() => {
		checkPatientsFade()
	}, [selectedPatients, checkPatientsFade])

	const appointmentId = mode === 'edit' && event ? event.id : null
	const { appointment: appointmentDetails, isLoading: isLoadingDetails } = useAppointmentDetail(appointmentId)

	const isEventToday = useMemo(() => {
		if (!appointmentDetails) return false
		const today = new Date()
		const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
		return appointmentDetails.date === todayStr
	}, [appointmentDetails])

	const patientPersonalIds = useMemo(
		() => selectedPatients.map((p) => p.personalId),
		[selectedPatients],
	)
	const { data: dailyNotesStatus } = trpc.calendar.dailyNotes.checkPatients.useQuery(
		{ personalIds: patientPersonalIds, calendarType },
		{ enabled: isEventToday && patientPersonalIds.length > 0 },
	)

	// Seed once per opened appointment: a background refetch must not wipe
	// ticks the user hasn't saved yet
	const seededCheckInsFor = useRef<string | null>(null)
	useEffect(() => {
		if (!isEventToday || !appointmentDetails) {
			seededCheckInsFor.current = null
			return
		}
		if (seededCheckInsFor.current === appointmentDetails.id) return
		seededCheckInsFor.current = appointmentDetails.id
		// Ticked = checked in on THIS appointment (the weekly report's
		// attendance). Being in today's Denní zápis for another reason is
		// shown by the "(DZ)" label instead — pre-ticking from it would turn
		// any later save into a check-in. addPatients skips patients already
		// in today's notes for this calendar, so re-ticking doesn't duplicate
		// this calendar's note.
		const checked = new Set<string>()
		for (const p of appointmentDetails.patientDetails) {
			if (p.checkedIn) checked.add(p.personalId)
		}
		setPatientsToDailyNotes(checked)
	}, [isEventToday, appointmentDetails])

	const addToDailyNotesMutation = trpc.calendar.dailyNotes.addPatients.useMutation({
		// The appointment (and its check-ins) is already saved at this point —
		// say so, otherwise the patient silently has no Denní zápis entry
		onError: (error) => {
			toast.error(`Termín uložen, ale pacienta se nepodařilo zapsat do Denního zápisu: ${error.message}`)
		},
	})

	const { data: cancelSmsData } = trpc.sms.getCancelForAppointment.useQuery(
		{ appointmentId: appointmentId! },
		{ enabled: !!appointmentId },
	)

	const createCancelSmsMutation = trpc.sms.create.useMutation()
	const trpcUtils = trpc.useUtils()

	const createMutation = useCreateAppointment()
	const updateMutation = useUpdateAppointment()
	const deleteMutation = useDeleteAppointment()

	const isLoading =
		createMutation.isPending ||
		updateMutation.isPending ||
		deleteMutation.isPending

	const {
		register,
		handleSubmit,
		reset,
		watch,
		setValue,
		formState: { errors },
	} = useForm<AppointmentFormValues>({
		resolver: zodResolver(appointmentFormSchema),
		defaultValues: {
			title: '',
			date: formatDateForApi(new Date()),
			startTime: '09:00',
			endTime: '10:00',
			note: '',
			doctorsNote: '',
			background: DEFAULT_EVENT_COLOR,
			repeatAfter: 'NONE',
			endDate: '',
		},
	})

	const background = watch('background')
	const selectedDate = watch('date')
	const startTime = watch('startTime')
	const endTime = watch('endTime')
	const actionTypes = getActionTypes(calendarType)

	const isNoShowBackground = background === STATUS_BACKGROUNDS.NO_SHOW || background === 'rgb(213, 0, 0)' || background === '#d50000'

	const isCancelledBg = isCancelledOnly(background ?? null)

	const ppCountForSelectedDate = selectedDate ? (dayStats[selectedDate]?.ppCount ?? 0) : 0
	const isPPDisabled = ppCountForSelectedDate >= MAX_PP_PER_DAY

	const { data: patientSearchResults, isLoading: isSearching } = trpc.patient.search.useQuery(
		{ query: patientQuery, limit: 10 },
		{ enabled: patientQuery.length >= 2, staleTime: 10000 },
	)

	const filteredPatients = (patientSearchResults || []).filter(
		(p) => !selectedPatients.some((sp) => sp.id === p.id),
	)

	useEffect(() => {
		if (!open) return

		if (mode === 'edit' && appointmentDetails) {
			reset({
				title: appointmentDetails.title || '',
				date: appointmentDetails.date,
				startTime: appointmentDetails.startTime,
				endTime: appointmentDetails.endTime,
				note: appointmentDetails.note || '',
				doctorsNote: appointmentDetails.doctorsNote || '',
				background: appointmentDetails.background || DEFAULT_EVENT_COLOR,
				repeatAfter: appointmentDetails.repeatAfter,
				endDate: appointmentDetails.endDate || '',
			})
			const patients = appointmentDetails.patientDetails.map((p) => ({
				id: p.id,
				personalId: p.personalId,
				firstName: p.firstName,
				lastName: p.lastName,
			}))
			setSelectedPatients(patients)
			const pData: Record<string, PatientRowData> = {}
			for (const p of appointmentDetails.patientDetails) {
				pData[p.id] = {
					actionType: p.actionType || appointmentDetails.actionType || '',
					priceFrom: appointmentDetails.priceFrom?.toString() || '',
					priceTo: appointmentDetails.priceTo?.toString() || '',
				}
			}
			setPatientData(pData)
			setNoShowUpPaid(appointmentDetails.noShowUpPaid || false)
		} else if (mode === 'create' && defaults) {
			reset({
				title: '',
				date: defaults.date ? formatDateForApi(defaults.date) : formatDateForApi(new Date()),
				startTime: defaults.startTime || '09:00',
				endTime: defaults.endTime || addMinutesToTime(defaults.startTime || '09:00', 60),
				note: '',
				doctorsNote: '',
				background: DEFAULT_EVENT_COLOR,
				repeatAfter: 'NONE',
				endDate: '',
			})
			setSelectedPatients([])
			setPatientData({})
			setNoShowUpPaid(false)
			setSendCancelSms(false)
			setCancelType('')
		} else {
			reset({
				title: '',
				date: formatDateForApi(new Date()),
				startTime: '09:00',
				endTime: '10:00',
				note: '',
				doctorsNote: '',
				background: DEFAULT_EVENT_COLOR,
				repeatAfter: 'NONE',
				endDate: '',
			})
			setSelectedPatients([])
			setPatientData({})
			setNoShowUpPaid(false)
			setSendCancelSms(false)
			setCancelType('')
		}
		setPatientQuery('')
		setPatientDropdownOpen(false)
	}, [open, mode, appointmentDetails, defaults, reset])

	const handleAddPatient = useCallback((patient: PatientSearchResult) => {
		setSelectedPatients((prev) => {
			if (prev.some((p) => p.id === patient.id)) return prev
			const newPatients = [...prev, patient]
			setValue('title', generateTitleFromPatients(newPatients))
			return newPatients
		})
		setPatientData((prev) => ({
			...prev,
			[patient.id]: { actionType: '', priceFrom: '', priceTo: '' },
		}))
		setPatientQuery('')
		setPatientDropdownOpen(false)
	}, [setValue])

	const handleRemovePatient = useCallback((patientId: string) => {
		setSelectedPatients((prev) => {
			const removed = prev.find((p) => p.id === patientId)
			if (removed) {
				// Re-adding the patient must not bring back a stale check-in tick
				setPatientsToDailyNotes((ticked) => {
					const next = new Set(ticked)
					next.delete(removed.personalId)
					return next
				})
			}
			const newPatients = prev.filter((p) => p.id !== patientId)
			setValue('title', generateTitleFromPatients(newPatients))
			return newPatients
		})
		setPatientData((prev) => {
			const { [patientId]: _, ...rest } = prev
			return rest
		})
	}, [setValue])

	const handlePatientDataChange = useCallback((patientId: string, field: keyof PatientRowData, value: string) => {
		setPatientData((prev) => ({
			...prev,
			[patientId]: {
				...prev[patientId],
				[field]: value,
			},
		}))
	}, [])

	const dailyNotesColor = CALENDAR_COLORS[2].color

	const handleToggleDailyNotes = useCallback((personalId: string) => {
		setPatientsToDailyNotes((prev) => {
			const newSet = new Set(prev)
			if (newSet.has(personalId)) {
				newSet.delete(personalId)
			} else {
				newSet.add(personalId)
				setValue('background', dailyNotesColor)
			}
			return newSet
		})
	}, [setValue, dailyNotesColor])

	const handlePatientKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (!patientDropdownOpen || filteredPatients.length === 0) return

			switch (e.key) {
				case 'ArrowDown':
					e.preventDefault()
					setPatientSelectedIndex((prev) => (prev + 1) % filteredPatients.length)
					break
				case 'ArrowUp':
					e.preventDefault()
					setPatientSelectedIndex((prev) => (prev - 1 + filteredPatients.length) % filteredPatients.length)
					break
				case 'Enter':
					e.preventDefault()
					if (filteredPatients[patientSelectedIndex]) {
						handleAddPatient(filteredPatients[patientSelectedIndex])
					}
					break
				case 'Escape':
					setPatientDropdownOpen(false)
					break
			}
		},
		[patientDropdownOpen, filteredPatients, patientSelectedIndex, handleAddPatient],
	)

	const handleStartTimeChange = useCallback(
		(next: string) => {
			setValue('startTime', next, { shouldValidate: true, shouldDirty: true })
			if (!TIME_REGEX.test(next) || !TIME_REGEX.test(endTime || '')) return
			const startMin = timeToMinutes(next)
			const endMin = timeToMinutes(endTime)
			if (endMin <= startMin) {
				const prevStart = TIME_REGEX.test(startTime || '') ? timeToMinutes(startTime) : startMin
				const duration = Math.max(15, endMin - prevStart)
				setValue('endTime', addMinutesToTime(next, duration), {
					shouldValidate: true,
					shouldDirty: true,
				})
			}
		},
		[setValue, startTime, endTime],
	)

	const handleEndTimeChange = useCallback(
		(next: string) => {
			setValue('endTime', next, { shouldValidate: true, shouldDirty: true })
		},
		[setValue],
	)

	const getAggregatedPatientData = useCallback(() => {
		const entries = Object.values(patientData)
		if (entries.length === 0) return { actionType: null, priceFrom: null, priceTo: null }

		const actionType = entries[0].actionType || null

		let priceFrom: number | null = null
		let priceTo: number | null = null
		for (const entry of entries) {
			if (entry.priceFrom) {
				priceFrom = (priceFrom ?? 0) + parseFloat(entry.priceFrom)
			}
			if (entry.priceTo) {
				priceTo = (priceTo ?? 0) + parseFloat(entry.priceTo)
			}
		}

		return { actionType, priceFrom, priceTo }
	}, [patientData])

	const onSubmit = async (data: AppointmentFormValues) => {
		const aggregated = getAggregatedPatientData()

		const patientEntries = selectedPatients.map((p) => ({
			personalId: p.personalId,
			actionType: patientData[p.id]?.actionType || null,
		}))

		const formData: AppointmentFormData = {
			title: data.title,
			date: new Date(data.date),
			startTime: data.startTime,
			endTime: data.endTime,
			patientIds: selectedPatients.map((p) => p.personalId),
			patients: patientEntries,
			priceFrom: aggregated.priceFrom,
			priceTo: aggregated.priceTo,
			note: data.note || null,
			doctorsNote: viewMode !== 'reception' ? (data.doctorsNote || null) : undefined,
			background: data.background || null,
			actionType: aggregated.actionType,
			repeatAfter: data.repeatAfter as RepeatFrequency,
			endDate: data.endDate ? new Date(data.endDate) : null,
			smsRequired: null,
			type: calendarType,
			noShowUpPaid: isNoShowBackground ? noShowUpPaid : false,
			// The checkbox only exists on today's appointments — elsewhere (or
			// when the dialog moves it to another day) leave stored check-ins
			// alone. Read by the weekly report.
			checkedInPersonalIds:
				isEventToday && data.date === appointmentDetails?.date
				? selectedPatients
						.map((p) => p.personalId)
						.filter((personalId) => patientsToDailyNotes.has(personalId))
				: undefined,
		}

		try {
			let savedAppointmentId = event?.id

			if (mode === 'create') {
				const result = await createMutation.mutateAsync(transformFormDataForCreate(formData))
				savedAppointmentId = result.id
			} else if (event) {
				await updateMutation.mutateAsync(
					transformFormDataForUpdate(event.id, formData),
				)
			}

			if (isCancelledBg && sendCancelSms && cancelType && savedAppointmentId && selectedPatients.length > 0) {
				const cancelSmsPromises = selectedPatients.map(async (patient) => {
					const phoneResult = await trpcUtils.sms.getPhoneFromQuestionnaire.fetch({
						patientId: patient.id,
					})
					const phone = phoneResult?.phone
					if (!phone) return

					return createCancelSmsMutation.mutateAsync({
						name: `${patient.lastName} ${patient.firstName}`,
						phone,
						appointmentId: savedAppointmentId,
						termin: {
							datum: data.date,
							casOd: data.startTime,
							cenaOd: aggregated.priceFrom ?? 0,
							cenaDo: aggregated.priceTo ?? undefined,
						},
						personalId: patient.personalId,
						isRegistered: true,
						isCancel: true,
						cancelType: cancelType as 'call' | 'sms' | 'person' | 'email',
						type: calendarType === 'OTHER' ? 'ZUB' : calendarType,
					})
				})
				await Promise.all(cancelSmsPromises)
			}

			if (isEventToday && patientsToDailyNotes.size > 0) {
				await addToDailyNotesMutation.mutateAsync({
					personalIds: Array.from(patientsToDailyNotes),
					calendarType,
				})
			}

			onClose()
		} catch {
			// Error is handled by mutation
		}
	}

	const handleDelete = async () => {
		if (!event || !window.confirm('Opravdu chcete smazat tento termín?')) return

		setIsDeleting(true)
		try {
			await deleteMutation.mutateAsync({ id: event.id })
			onClose()
		} catch {
			// Error is handled by mutation
		} finally {
			setIsDeleting(false)
		}
	}

	const submitForm = handleSubmit(onSubmit)

	return {
		// Props passthrough
		open,
		onClose,
		mode,
		viewMode,
		calendarType,

		// Form
		register,
		errors,
		setValue,
		submitForm,

		// Watched form values
		background,
		selectedDate,
		startTime,
		endTime,
		handleStartTimeChange,
		handleEndTimeChange,

		// Loading flags
		isLoading,
		isLoadingDetails,
		isDeleting,
		isSearching,

		// Derived state
		isEventToday,
		isNoShowBackground,
		isCancelledBg,
		isPPDisabled,
		actionTypes,

		// Patient selection
		selectedPatients,
		patientData,
		filteredPatients,
		patientsListRef,
		showPatientsFade,
		checkPatientsFade,
		handleAddPatient,
		handleRemovePatient,
		handlePatientDataChange,

		// Patient search
		patientQuery,
		setPatientQuery,
		patientDropdownOpen,
		setPatientDropdownOpen,
		patientSelectedIndex,
		setPatientSelectedIndex,
		handlePatientKeyDown,

		// Daily notes
		patientsToDailyNotes,
		dailyNotesStatus,
		handleToggleDailyNotes,

		// No-show
		noShowUpPaid,
		setNoShowUpPaid,

		// Cancel SMS
		sendCancelSms,
		setSendCancelSms,
		cancelType,
		setCancelType,
		cancelSmsData,

		// Delete
		handleDelete,

		// Ghost column
		showGhostColumn,
		toggleGhostColumn,
	}
}

export type AppointmentDialogState = ReturnType<typeof useAppointmentDialog>
